'use client';

import { useCallback, useEffect, useRef } from 'react';
import { importLibrary } from '@googlemaps/js-api-loader';
import { MapCanvas } from '@/components/organisms/GoogleMap/MapCanvas';

/**
 * Street-level zoom for the "Find it" rail card (WEB-860 AC 3). Tight enough
 * that the pin reads as *this building* rather than *this district* — which
 * matters most now that a venue with no stored coordinates renders no map at
 * all rather than a city-centre fallback.
 */
export const MINI_MAP_ZOOM = 16;

export interface VenueMiniMapProps {
  apiKey: string;
  /**
   * Stored venue latitude. Required and non-nullable by design: a venue with
   * no `Location.coordinates` must render NO map, not a map centred on a
   * substitute. The caller makes that decision (WEB-860) — see
   * `VenueDetailTemplate`, which passes `mapSlot={null}` in that branch.
   */
  latitude: number;
  /** Stored venue longitude. See `latitude`. */
  longitude: number;
  venueName: string;
}

export function VenueMiniMap({ apiKey, latitude, longitude, venueName }: VenueMiniMapProps) {
  const markerRef = useRef<google.maps.marker.AdvancedMarkerElement | null>(null);

  useEffect(() => {
    return () => {
      if (markerRef.current) {
        markerRef.current.map = null;
        markerRef.current = null;
      }
    };
  }, []);

  const handleMapLoad = useCallback(
    async (map: google.maps.Map) => {
      const { AdvancedMarkerElement } = (await importLibrary(
        'marker',
      )) as google.maps.MarkerLibrary;

      if (markerRef.current) {
        markerRef.current.map = null;
      }

      markerRef.current = new AdvancedMarkerElement({
        map,
        position: { lat: latitude, lng: longitude },
        title: venueName,
      });
    },
    [latitude, longitude, venueName],
  );

  return (
    <section aria-label="Venue location map" className="h-full w-full">
      <MapCanvas
        apiKey={apiKey}
        onMapLoad={handleMapLoad}
        center={{ lat: latitude, lng: longitude }}
        zoom={MINI_MAP_ZOOM}
      />
    </section>
  );
}
